import { useCallback, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  useGetPendingInvitesQuery,
  useGetUsersQuery,
} from "store/api/generatedApi";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import {
  filterInvites,
  filterUsers,
  InviteRow,
  QuickFilterKey,
  UserDrawerTarget,
  UserRow,
} from "./userAdminFilters";
import { useUserAdminActions } from "./userAdminActions";

export const useUserAdminState = ({ tenant }: { tenant: string }) => {
  const { data: usersData, isFetching: isUsersFetching } = useGetUsersQuery(
    tenant
      ? { tenantName: tenant, accountTypes: ["user", "service_account"] }
      : skipToken
  );
  const { data: invitesData, isFetching: isInvitesFetching } =
    useGetPendingInvitesQuery(tenant ? { tenantName: tenant } : skipToken);

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedValue(searchText, 200);
  const [quickFilters, setQuickFilters] = useState<ReadonlySet<QuickFilterKey>>(
    () => new Set()
  );
  const [usersSelection, setUsersSelection] = useState<string[]>([]);
  const [serviceAccountsSelection, setServiceAccountsSelection] = useState<
    string[]
  >([]);
  const [invitesSelection, setInvitesSelection] = useState<string[]>([]);
  const [drawerSelection, setDrawerSelection] = useState<
    { kind: "user"; uid: string } | { kind: "invite"; email: string } | null
  >(null);

  const allUsers = useMemo(
    () => (usersData ?? []).map((user): UserRow => ({ ...user, id: user.uid })),
    [usersData]
  );
  const userById = useMemo(
    () => new Map(allUsers.map((user) => [user.uid, user])),
    [allUsers]
  );

  const users = useMemo(
    () => allUsers.filter((user) => user.account_type !== "service_account"),
    [allUsers]
  );
  const serviceAccounts = useMemo(
    () => allUsers.filter((user) => user.account_type === "service_account"),
    [allUsers]
  );

  const invites = useMemo(
    () =>
      (invitesData ?? []).map((invite): InviteRow => ({
        ...invite,
        id: invite.email,
      })),
    [invitesData]
  );

  const filteredUsers = useMemo(
    () => filterUsers(users, debouncedSearch, quickFilters),
    [users, debouncedSearch, quickFilters]
  );
  const filteredServiceAccounts = useMemo(
    () => filterUsers(serviceAccounts, debouncedSearch, quickFilters),
    [serviceAccounts, debouncedSearch, quickFilters]
  );
  const filteredInvites = useMemo(
    () => filterInvites(invites, debouncedSearch),
    [invites, debouncedSearch]
  );

  const toggleQuickFilter = useCallback((key: QuickFilterKey) => {
    setQuickFilters((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const inviteByEmail = useMemo(
    () => new Map(invites.map((invite) => [invite.email, invite])),
    [invites]
  );

  const actions = useUserAdminActions({ tenant, userById });

  const drawerTarget = ((): UserDrawerTarget | null => {
    if (drawerSelection?.kind === "user") {
      const user = userById.get(drawerSelection.uid);
      return user ? { kind: "user", user } : null;
    }
    if (drawerSelection?.kind === "invite") {
      const invite = inviteByEmail.get(drawerSelection.email);
      return invite ? { kind: "invite", invite } : null;
    }
    return null;
  })();

  const counts = useMemo(
    () => ({
      users: users.length,
      serviceAccounts: serviceAccounts.length,
      invites: invites.length,
    }),
    [users, serviceAccounts, invites]
  );

  return {
    isUsersFetching,
    isInvitesFetching,
    searchText,
    setSearchText,
    quickFilters,
    toggleQuickFilter,
    filteredUsers,
    filteredServiceAccounts,
    filteredInvites,
    counts,
    usersSelection,
    setUsersSelection,
    serviceAccountsSelection,
    setServiceAccountsSelection,
    invitesSelection,
    setInvitesSelection,
    drawerTarget,
    drawerKey:
      drawerSelection?.kind === "user"
        ? drawerSelection.uid
        : (drawerSelection?.email ?? "closed"),
    openDrawer: useCallback((uid: string) => {
      setDrawerSelection({ kind: "user", uid });
    }, []),
    openInviteDrawer: useCallback((email: string) => {
      setDrawerSelection({ kind: "invite", email });
    }, []),
    closeDrawer: useCallback(() => {
      setDrawerSelection(null);
    }, []),
    ...actions,
  };
};

export type UserAdminState = ReturnType<typeof useUserAdminState>;
